<template>
  <IntercomLayout>
    <template #topbar>
      <FaraidTopbar
        :business-name="businessName"
        :user="user"
        :global-user="globalUser"
        @settings="$router.push('/settings')"
        @logout="handleLogout"
      />
    </template>

    <template #sidebar>
      <IntercomSidebar :nav-sections="navSections" />
    </template>

    <div class="scholars-view">
      <!-- Page Header -->
      <div class="page-header">
        <div>
          <h1 class="page-title">{{ $t('scholar.scholars') }}</h1>
          <p class="page-subtitle">{{ $t('scholar.subtitle') }}</p>
        </div>
        <IntercomButton variant="primary" @click="openAddModal">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          <span>{{ $t('scholar.addScholar') }}</span>
        </IntercomButton>
      </div>

      <!-- Table -->
      <IntercomCard>
        <IntercomTable
          :columns="columns"
          :data="scholars"
          :loading="isLoading"
          hoverable
        >
          <template #cell-is_active="{ value }">
            <IntercomBadge :variant="value ? 'success' : 'default'">
              {{ value ? $t('common.active') : $t('common.inactive') }}
            </IntercomBadge>
          </template>
          <template #cell-school_specialization="{ value }">
            {{ value === 'hanafi' ? $t('calculator.hanafi') : $t('calculator.shiaIthnaAshari') }}
          </template>
          <template #cell-actions="{ row }">
            <div class="row-actions">
              <IntercomButton variant="ghost" size="sm" @click="openEditModal(row)">
                {{ $t('common.edit') }}
              </IntercomButton>
              <IntercomButton
                v-if="row.is_active"
                variant="ghost"
                size="sm"
                @click="handleDeactivate(row)"
              >
                {{ $t('scholar.deactivate') }}
              </IntercomButton>
            </div>
          </template>
        </IntercomTable>
        <p v-if="!isLoading && scholars.length === 0" class="empty-text">
          {{ $t('scholar.noScholars') }}
        </p>
      </IntercomCard>

      <!-- Add/Edit Modal -->
      <IntercomModal
        v-model:show="showModal"
        :title="editingScholar ? $t('scholar.editScholar') : $t('scholar.addScholar')"
      >
        <ScholarForm
          :scholar="editingScholar"
          :loading="isSaving"
          @submit="handleSave"
          @cancel="closeModal"
        />
      </IntercomModal>
    </div>
  </IntercomLayout>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useDialog } from 'naive-ui'
import { useToast } from '@shared/composables/useToast.js'
import { useAuth } from '@shared/composables/useAuth.js'
import { useBusiness } from '@shared/composables/useBusiness.js'
import { useFaraid } from '../composables/useFaraid.js'
import { useNavigation } from '../composables/useNavigation.js'
import {
  IntercomLayout,
  IntercomSidebar,
  IntercomButton,
  IntercomCard,
  IntercomTable,
  IntercomBadge,
  IntercomModal,
} from '@shared/components/intercom'
import FaraidTopbar from '../components/FaraidTopbar.vue'
import ScholarForm from '../components/ScholarForm.vue'

const { t } = useI18n()
const router = useRouter()
const toast = useToast()
const dialog = useDialog()
const { user, globalUser, logout } = useAuth()
const { businessName, fetchBusiness } = useBusiness()
const { listScholars, createScholar, updateScholar, deactivateScholar } = useFaraid()
const { navSections } = useNavigation()

const scholars = ref([])
const isLoading = ref(false)
const showModal = ref(false)
const editingScholar = ref(null)
const isSaving = ref(false)

const columns = [
  { key: 'scholar_number', title: t('scholar.scholarNumber'), width: '120px' },
  { key: 'name', title: t('scholar.name'), width: '160px' },
  { key: 'institution', title: t('scholar.institution'), width: '150px' },
  { key: 'school_specialization', title: t('scholar.specialization'), width: '120px' },
  { key: 'cases_reviewed_count', title: t('scholar.casesReviewed'), width: '100px', align: 'center' },
  { key: 'is_active', title: t('scholar.status'), width: '90px' },
  { key: 'actions', title: t('common.actions'), width: '160px' },
]

const fetchScholars = async () => {
  isLoading.value = true
  try {
    const response = await listScholars({ limit: 100 })
    scholars.value = response.data || response.items || []
  } catch (error) {
    console.error('Failed to fetch scholars:', error)
  } finally {
    isLoading.value = false
  }
}

const openAddModal = () => {
  editingScholar.value = null
  showModal.value = true
}

const openEditModal = (scholar) => {
  editingScholar.value = scholar
  showModal.value = true
}

const closeModal = () => {
  showModal.value = false
  editingScholar.value = null
}

const handleSave = async (formData) => {
  isSaving.value = true
  try {
    if (editingScholar.value) {
      await updateScholar(editingScholar.value.id, formData)
      toast.success(t('success.scholarUpdated'))
    } else {
      await createScholar(formData)
      toast.success(t('success.scholarCreated'))
    }
    closeModal()
    fetchScholars()
  } catch (error) {
    toast.error(error.message || t('error.saveFailed'))
  } finally {
    isSaving.value = false
  }
}

const handleDeactivate = (scholar) => {
  dialog.warning({
    title: t('scholar.deactivate'),
    content: t('scholar.deactivateConfirm'),
    positiveText: t('common.confirm'),
    negativeText: t('common.cancel'),
    onPositiveClick: async () => {
      try {
        await deactivateScholar(scholar.id)
        toast.success(t('success.scholarDeactivated'))
        fetchScholars()
      } catch (error) {
        toast.error(error.message)
      }
    },
  })
}

const handleLogout = async () => {
  try {
    await logout()
    router.push('/login')
  } catch (error) {
    router.push('/login')
  }
}

onMounted(() => {
  fetchBusiness()
  fetchScholars()
})
</script>

<style scoped>
@import '@shared/styles/intercom-tokens.css';
@import '@shared/styles/intercom-base.css';

.scholars-view {
  padding: var(--space-8);
  max-width: var(--content-max-width);
  margin: 0 auto;
}

.page-header {
  margin-bottom: var(--space-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.page-title {
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-charcoal);
  margin: 0;
}

.page-subtitle {
  font-size: var(--font-size-base);
  color: var(--color-gray-500);
  margin-top: var(--space-1);
  margin-bottom: 0;
}

.row-actions {
  display: flex;
  gap: var(--space-1);
}

.empty-text {
  text-align: center;
  color: var(--color-gray-500);
  padding: var(--space-8);
}
</style>
